/*eslint-disable no-unused-vars */
/*eslint-disable react-hooks/exhaustive-deps*/
import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Badge, Button, Col, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Table } from 'reactstrap';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useTranslation } from 'react-i18next';

import { setLoadingOverlay } from '../../store/slices/loadingOverlaySlice';
import LocalstorageService from '../../helpers/localstorage-service';
import { CompanyId } from '../../constants';
import profitabilityService from '../../services/profitability';
import NoRecords from '../../components/Table/NoRecords';

const MONTHS_HE = ['', 'ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
  'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];

const METHOD_COLOR = { 1: 'warning', 2: 'info', 3: 'success' };

const fmt = (v) => (v == null ? '—' : Number(v).toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

// ── יצוא אקסל ──────────────────────────────────────────────────
const fmtAmt = (v) => Number.isInteger(v) ? v : parseFloat(v.toFixed(2));
const fmtAmtStr = (v) => Number.isInteger(v) ? String(v) : v.toFixed(2);

const buildExcel = (rows, month, year, minAmount, t) => {
  const monthName = MONTHS_HE[month];

  // שיקים בלבד (paymentMethod=1), מעל סכום מינימום
  // חשוב: paymentMethod=0 = "ללא תשלום" — חייב לסנן מפורשות (0 הוא falsy ב-JS!)
  const filtered = rows.filter(r => {
    const pm = r.paymentMethod == null ? 1 : Number(r.paymentMethod);
    if (pm !== 1) return false;
    const total = (r.fixPayment || 0) + (r.estimatedPerBeat || 0);
    return total > minAmount;
  });

  if (filtered.length === 0) {
    toast.warning(t('PROFITABILITY.PAYMENT_SCHEDULE_PAGE.TOAST_NO_ROWS'));
    return;
  }

  // ── הפרדה: ריכוזים vs. מסופים בודדים ──────────────────────
  const groupMap = new Map();   // locationGroupId → [rows]
  const singles  = [];

  for (const r of filtered) {
    if (r.locationGroupId) {
      if (!groupMap.has(r.locationGroupId)) groupMap.set(r.locationGroupId, []);
      groupMap.get(r.locationGroupId).push(r);
    } else {
      singles.push(r);
    }
  }

  // ── בניית שורות סופיות ─────────────────────────────────────
  const outRows = [];

  // מסופים בודדים
  for (const r of singles) {
    const total     = fmtAmt((r.fixPayment || 0) + (r.estimatedPerBeat || 0));
    const checkName = (r.checkForName || r.locationName || '').trim();
    const locName   = (r.locationName || '').trim();
    const prefix    = checkName && checkName !== locName
      ? `${locName} (${checkName})`
      : locName;
    outRows.push({
      terminal     : r.terminalNumber,
      employeeName : (r.employeeName || '').trim(),
      checkName,
      locName,
      total,
      desc         : `${prefix} עמלות כספומט חודש - ${monthName}`,
    });
  }

  // ריכוזים — שורה אחת לכל קבוצה
  for (const groupRows of groupMap.values()) {
    const first     = groupRows[0];
    const terminal  = first.groupCode || first.terminalNumber;
    // שם על השיק: מהריכוז עצמו (GroupCheckForName) — אם קיים, אחרת fallback למסוף הראשון
    const checkName = (first.groupCheckForName || first.checkForName || first.locationName || '').trim();
    const rawTotal  = groupRows.reduce(
      (s, r) => s + (r.fixPayment || 0) + (r.estimatedPerBeat || 0), 0);
    const total     = fmtAmt(rawTotal);

    // תיאור: שם מסוף1 סכום1, שם מסוף2 סכום2 עמלות כספומט חודש - חודש
    const parts = [...groupRows]
      .sort((a, b) => (a.locationName || '').localeCompare(b.locationName || '', 'he'))
      .map(r => {
        const t = fmtAmt((r.fixPayment || 0) + (r.estimatedPerBeat || 0));
        return `${(r.locationName || '').trim()} ${fmtAmtStr(t)}`;
      });
    outRows.push({
      terminal     : first.groupCode || first.terminalNumber,
      employeeName : (first.employeeName || '').trim(),
      checkName,
      locName      : first.groupName || checkName,
      total,
      desc         : `${parts.join(', ')} עמלות כספומט חודש - ${monthName}`,
    });
  }

  // ── מיון: עובד (א-ת) → שם מקום ────────────────────────────
  outRows.sort((a, b) => {
    const ce = (a.employeeName || '\uFFFF').localeCompare(b.employeeName || '\uFFFF', 'he');
    if (ce !== 0) return ce;
    return (a.locName || '').localeCompare(b.locName || '', 'he');
  });

  // ── כתיבה לגיליון ──────────────────────────────────────────
  const sheetData = outRows.map(r => [
    r.terminal,   // A — מספר מסוף / קוד ריכוז
    null,         // B — ריק
    r.checkName,  // C — שם על השיק
    r.total,      // D — סכום
    r.desc,       // E — תיאור
  ]);

  const ws = XLSX.utils.aoa_to_sheet(sheetData);

  // פורמט מספרים: שלם ← ללא נקודה, עשרוני ← שתי ספרות
  outRows.forEach((r, i) => {
    const cell = ws[`D${i + 1}`];
    if (cell) cell.z = Number.isInteger(r.total) ? '#,##0' : '#,##0.00';
  });

  ws['!cols'] = [
    { wch: 12 },  // A
    { wch: 4  },  // B
    { wch: 35 },  // C
    { wch: 12 },  // D
    { wch: 65 },  // E
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'שקים');
  const buf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
  saveAs(
    new Blob([buf], { type: 'application/octet-stream' }),
    `שקים_${monthName}_${year}.xlsx`
  );
  toast.success(t('PROFITABILITY.PAYMENT_SCHEDULE_PAGE.TOAST_EXPORTED', { count: outRows.length }));
};

// ── רכיב ──────────────────────────────────────────────────────
const PaymentSchedule = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [setPageTitle, setCrumbItem] = useOutletContext();

  const companyId = LocalstorageService.getItem(CompanyId);
  const now = new Date();

  const FREQ_LABEL  = {
    1:  t('PROFITABILITY.PAYMENT_SCHEDULE_PAGE.FREQ.MONTHLY'),
    3:  t('PROFITABILITY.PAYMENT_SCHEDULE_PAGE.FREQ.QUARTERLY'),
    6:  t('PROFITABILITY.PAYMENT_SCHEDULE_PAGE.FREQ.SEMI_ANNUAL'),
    12: t('PROFITABILITY.PAYMENT_SCHEDULE_PAGE.FREQ.ANNUAL'),
  };
  const METHOD_LABEL = {
    1: t('PROFITABILITY.PAYMENT_SCHEDULE_PAGE.METHOD.CHECK'),
    2: t('PROFITABILITY.PAYMENT_SCHEDULE_PAGE.METHOD.BANK_TRANSFER'),
    3: t('PROFITABILITY.PAYMENT_SCHEDULE_PAGE.METHOD.CASH'),
  };

  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year,  setYear]  = useState(now.getFullYear());
  const [list,  setList]  = useState([]);

  // מודל יצוא
  const [exportModal, setExportModal] = useState(false);
  const [minAmount,   setMinAmount]   = useState(0);

  useEffect(() => {
    setPageTitle(t('PROFITABILITY.PAYMENT_SCHEDULE_PAGE.PAGE_TITLE'));
    setCrumbItem([
      { title: t('PROFITABILITY.PAYMENT_SCHEDULE_PAGE.CRUMB_PARENT'), link: '#' },
      { title: t('PROFITABILITY.PAYMENT_SCHEDULE_PAGE.CRUMB_ITEM'), link: '#' },
    ]);
  }, []);

  const fetchSchedule = async () => {
    dispatch(setLoadingOverlay(true));
    try {
      const res = await profitabilityService.getPaymentSchedule({ companyId, month, year });
      setList(res.data?.dataList || []);
    } catch {
      toast.error(t('PROFITABILITY.PAYMENT_SCHEDULE_PAGE.TOAST_ERROR_LOAD'));
    } finally {
      dispatch(setLoadingOverlay(false));
    }
  };

  useEffect(() => { fetchSchedule(); }, [month, year]);

  const yearOptions = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 5; y--) yearOptions.push(y);

  const totalFix     = list.reduce((s, r) => s + (r.fixPayment     || 0), 0);
  const totalPerBeat = list.reduce((s, r) => s + (r.estimatedPerBeat || 0), 0);
  const totalAll     = list.reduce((s, r) => s + (r.fixPayment || 0) + (r.estimatedPerBeat || 0), 0);

  const byMethod = list.reduce((acc, r) => {
    const m = r.paymentMethod || 1;
    acc[m] = (acc[m] || 0) + (r.fixPayment || 0) + (r.estimatedPerBeat || 0);
    return acc;
  }, {});

  const handleExportConfirm = () => {
    setExportModal(false);
    buildExcel(list, month, year, Number(minAmount) || 0, t);
  };

  return (
    <React.Fragment>
      <Row className="mb-3 align-items-end">
        <Col xs="auto">
          <label className="form-label mb-1">{t('PROFITABILITY.COMMON.MONTH')}</label>
          <select className="form-select" value={month} onChange={e => setMonth(+e.target.value)}>
            {MONTHS_HE.slice(1).map((m, i) => (
              <option key={i + 1} value={i + 1}>{m}</option>
            ))}
          </select>
        </Col>
        <Col xs="auto">
          <label className="form-label mb-1">{t('PROFITABILITY.COMMON.YEAR')}</label>
          <select className="form-select" value={year} onChange={e => setYear(+e.target.value)}>
            {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </Col>
        <Col>
          <div className="text-muted text-end">
            {MONTHS_HE[month]} {year} — {list.length} {t('PROFITABILITY.PAYMENT_SCHEDULE_PAGE.SUMMARY').replace('{{month}} {{year}} — {{count}} ', '')}
          </div>
        </Col>
        {list.length > 0 && (
          <Col xs="auto">
            <Button color="success" size="sm" onClick={() => setExportModal(true)}>
              <i className="ri-file-excel-2-line me-1" />
              {t('PROFITABILITY.PAYMENT_SCHEDULE_PAGE.EXPORT_CHECKS')}
            </Button>
          </Col>
        )}
      </Row>

      {/* סיכום לפי שיטה */}
      {list.length > 0 && (
        <Row className="mb-3">
          {Object.entries(byMethod).map(([method, sum]) => (
            <Col key={method} xs="auto">
              <Badge color={METHOD_COLOR[method] || 'secondary'} className="p-2 fs-6">
                {METHOD_LABEL[method] || method}: {fmt(sum)} ₪
              </Badge>
            </Col>
          ))}
          <Col xs="auto">
            <Badge color="dark" className="p-2 fs-6">
              {t('PROFITABILITY.PAYMENT_SCHEDULE_PAGE.GRAND_TOTAL')}: {fmt(totalAll)} ₪
            </Badge>
          </Col>
        </Row>
      )}

      {list.length === 0 ? <NoRecords /> : (
        <div className="table-responsive" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
          <Table bordered hover size="sm">
            <thead className="table-dark">
              <tr>
                <th>{t('PROFITABILITY.PAYMENT_SCHEDULE_PAGE.TABLE.TERMINAL')}</th>
                <th>{t('PROFITABILITY.PAYMENT_SCHEDULE_PAGE.TABLE.LOCATION')}</th>
                <th>{t('PROFITABILITY.PAYMENT_SCHEDULE_PAGE.TABLE.FREQUENCY')}</th>
                <th>{t('PROFITABILITY.PAYMENT_SCHEDULE_PAGE.TABLE.PAYMENT_METHOD')}</th>
                <th className="text-end">{t('PROFITABILITY.PAYMENT_SCHEDULE_PAGE.TABLE.FIXED_PAYMENT')}</th>
                <th className="text-end">{t('PROFITABILITY.PAYMENT_SCHEDULE_PAGE.TABLE.PER_BEAT')}</th>
                <th className="text-end">{t('PROFITABILITY.PAYMENT_SCHEDULE_PAGE.TABLE.TOTAL_EXPECTED')}</th>
              </tr>
            </thead>
            <tbody>
              {list.map(r => (
                <tr key={r.terminalNumber || r.locationId}>
                  <td>{r.terminalNumber}</td>
                  <td>{r.locationName}</td>
                  <td>
                    <Badge color="secondary" className="small">
                      {FREQ_LABEL[r.paymentFrequency] || r.paymentFrequency}
                    </Badge>
                  </td>
                  <td>
                    <Badge color={METHOD_COLOR[r.paymentMethod] || 'secondary'} className="small">
                      {METHOD_LABEL[r.paymentMethod] || '—'}
                    </Badge>
                  </td>
                  <td className="text-end">{fmt(r.fixPayment)}</td>
                  <td className="text-end">{fmt(r.estimatedPerBeat)}</td>
                  <td className="text-end fw-bold">
                    {fmt((r.fixPayment || 0) + (r.estimatedPerBeat || 0))}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="table-secondary fw-bold">
                <td colSpan={4}>{t('PROFITABILITY.PAYMENT_SCHEDULE_PAGE.GRAND_TOTAL')}</td>
                <td className="text-end">{fmt(totalFix)}</td>
                <td className="text-end">{fmt(totalPerBeat)}</td>
                <td className="text-end">{fmt(totalAll)} ₪</td>
              </tr>
            </tfoot>
          </Table>
        </div>
      )}

      {/* מודל יצוא אקסל */}
      <Modal isOpen={exportModal} toggle={() => setExportModal(false)} centered>
        <ModalHeader toggle={() => setExportModal(false)}>
          {t('PROFITABILITY.PAYMENT_SCHEDULE_PAGE.EXPORT_MODAL.TITLE')} — {MONTHS_HE[month]} {year}
        </ModalHeader>
        <ModalBody>
          <Label for="minAmountInput">{t('PROFITABILITY.PAYMENT_SCHEDULE_PAGE.EXPORT_MODAL.MIN_AMOUNT_LABEL')}</Label>
          <Input
            id="minAmountInput"
            type="number"
            min={0}
            value={minAmount}
            onChange={e => setMinAmount(e.target.value)}
            placeholder={t('PROFITABILITY.PAYMENT_SCHEDULE_PAGE.EXPORT_MODAL.MIN_AMOUNT_PLACEHOLDER')}
          />
          <small className="text-muted d-block mt-1">
            {t('PROFITABILITY.PAYMENT_SCHEDULE_PAGE.EXPORT_MODAL.DEFAULT_NOTE')}
          </small>
          <small className="text-muted d-block mt-1">
            {t('PROFITABILITY.PAYMENT_SCHEDULE_PAGE.EXPORT_MODAL.CHECK_ONLY_NOTE')} <strong>{t('PROFITABILITY.PAYMENT_SCHEDULE_PAGE.EXPORT_MODAL.CHECK_ONLY_METHOD')}</strong>
          </small>
        </ModalBody>
        <ModalFooter>
          <Button color="success" onClick={handleExportConfirm}>
            <i className="ri-download-2-line me-1" />
            {t('PROFITABILITY.PAYMENT_SCHEDULE_PAGE.EXPORT_MODAL.EXPORT_BUTTON')}
          </Button>
          <Button color="secondary" outline onClick={() => setExportModal(false)}>
            {t('PROFITABILITY.COMMON.CANCEL')}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default PaymentSchedule;
